import React, { useState, useEffect } from 'react';
import {
    Plus, Search, Edit3, Trash2, CheckCircle,
    Clock, MapPin, Calendar, MoreVertical, X
} from 'lucide-react';
import api from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';

const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filter, setFilter] = useState('ongoing');
    const [formData, setFormData] = useState({
        id: null, project_name: '', client_id: '', site_address: '',
        total_budget: '', start_date: '', end_date: '', status: 'ongoing'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const [pRes, cRes] = await Promise.all([
            api.get('get_projects_full.php'),
            api.get('get_directory.php?type=client')
        ]);
        setProjects(pRes.data.projects);
        setClients(cRes.data.data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const endpoint = formData.id ? 'update_project.php' : 'save_project.php';
        const res = await api.post(endpoint, formData);
        if (res.data.status === 'success') {
            setIsModalOpen(false);
            resetForm();
            fetchData();
        }
    };
    //TODO: Implement Delete Logic with proper confirmation/error box
    const handleDelete = async (id) => {
        if (window.confirm("Delete project? This will permanently remove the project and its records.")) {
            try {
                const res = await api.post('delete_project.php', { id });
                if (res.data.status === 'success') {
                    alert(res.data.message || "Project deleted successfully.");
                    fetchData();
                } else {
                    alert(res.data.message || "Delete failed");
                }
            } catch (err) {
                if (err.response && err.response.data) {
                    const errorData = err.response.data;
                    if (errorData.status === 'blocked') {
                        let blockMsg = errorData.message + "\n\nLinked Records:\n";
                        if (errorData.dependencies && errorData.dependencies.length > 0) {
                            errorData.dependencies.forEach(dep => {
                                blockMsg += `- ${dep.component}: ${dep.count}\n`;
                            });
                        }
                        alert(blockMsg);
                    } else {
                        alert(errorData.message || "Delete failed");
                    }
                } else {
                    alert("Delete failed due to network or server error.");
                }
            }
        }
    };

    const resetForm = () => setFormData({ id: null, project_name: '', client_id: '', site_address: '', total_budget: '', start_date: '', end_date: '', status: 'ongoing' });

    const openEdit = (p) => {
        setFormData(p);
        setIsModalOpen(true);
    };
    const safeProjectName = encodeURIComponent(projects.project_name);
    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Site Projects</h1>
                    <p className="text-slate-500 font-medium">Manage your active and finished sites.</p>
                </div>
                <button onClick={() => { resetForm(); setIsModalOpen(true); }} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 shadow-xl">
                    <Plus size={18} /> Launch New Project
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex bg-slate-100 p-1 rounded-2xl w-fit border border-slate-200">
                <button onClick={() => setFilter('ongoing')} className={`px-8 py-3 rounded-xl font-bold transition-all ${filter === 'ongoing' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Ongoing</button>
                <button onClick={() => setFilter('completed')} className={`px-8 py-3 rounded-xl font-bold transition-all ${filter === 'completed' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Completed</button>
            </div>

            {/* Project Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.filter(p => p.status === filter).map(project => (
                    <div key={project.id} className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
                        <div className="flex justify-between items-start mb-6">
                            <div className={`p-3 rounded-2xl ${project.status === 'ongoing' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                {project.status === 'ongoing' ? <Clock size={20} /> : <CheckCircle size={20} />}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => openEdit(project)} className="p-2 text-slate-400 hover:text-indigo-600 transition-all"><Edit3 size={16} /></button>
                                <button onClick={() => handleDelete(project.id)} className="p-2 text-slate-400 hover:text-rose-600 transition-all"><Trash2 size={16} /></button>
                            </div>
                        </div>

                        <div>
                            {/* CLICKABLE PROJECT NAME ACTION */}
                            <button
                                onClick={() => navigate(`/site-intelligence/${project.id}/${encodeURIComponent(project.project_name)}`)}
                                className="text-base font-black uppercase italic tracking-tight text-slate-800 hover:text-indigo-600 text-left transition-all block"
                            >

                                {project.project_name}
                            </button>
                            <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-wider">Site Context ID: #{project.id}</p>
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">{project.client_name}</p>

                        <div className="space-y-4 border-t border-slate-50 pt-6">
                            <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                                <MapPin size={14} className="text-indigo-500" /> {project.site_address}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                                <Calendar size={14} className="text-indigo-500" /> {project.start_date}
                            </div>
                            <div className="mt-4 p-4 bg-slate-50 rounded-2xl flex justify-between items-center">
                                <span className="text-[10px] font-black text-slate-400 uppercase">Budget</span>
                                <span className="font-black text-slate-900">₹{parseFloat(project.total_budget).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL: ADD/EDIT */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <div className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12 overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center mb-10">
                            <h2 className="text-3xl font-black italic">{formData.id ? 'Modify' : 'Launch'} Project</h2>
                            <button onClick={() => setIsModalOpen(false)}><X /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-6">
                            <input required value={formData.project_name} placeholder="Project Name (e.g. Villa Virar)" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, project_name: e.target.value })} />

                            <select required value={formData.client_id} className="p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setFormData({ ...formData, client_id: e.target.value })}>
                                <option value="">Select Client from Directory...</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>

                            <input type="number" value={formData.total_budget} placeholder="Total Budget (Excl. GST)" className="p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setFormData({ ...formData, total_budget: e.target.value })} />

                            <select value={formData.status} className="p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                <option value="ongoing">Ongoing</option>
                                <option value="completed">Completed</option>
                            </select>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Start Date</label>
                                <input type="date" value={formData.start_date} className="w-full p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setFormData({ ...formData, start_date: e.target.value })} />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-4">Expected Completion</label>
                                <input type="date" value={formData.end_date} className="w-full p-4 bg-slate-50 rounded-2xl font-bold" onChange={e => setFormData({ ...formData, end_date: e.target.value })} />
                            </div>

                            <textarea value={formData.site_address} placeholder="Site Address" className="p-4 bg-slate-50 rounded-2xl font-bold col-span-2 h-32" onChange={e => setFormData({ ...formData, site_address: e.target.value })} />

                            <button type="submit" className="col-span-2 py-6 bg-indigo-600 text-white rounded-[32px] font-black text-xl shadow-xl transition-all active:scale-95">
                                {formData.id ? 'Update Site Details' : 'Initialize Project'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectList;